import { useEffect } from "react"
import { useAuthStore } from "@/stores/auth-store"
import { useSubscriptionStore } from "@/stores/subscription-store"

export interface SubscriptionVerificationResult {
  isVerifying: boolean
  hasAccess: boolean
  isCanceled: boolean
}

/**
 * Verifies the authenticated user's subscription against the backend.
 * Runs whenever the authenticated user changes. The webapp gate should treat
 * `isVerifying` as "don't decide yet" to avoid flashing the paywall.
 */
export function useSubscriptionVerification(): SubscriptionVerificationResult {
  const authStatus = useAuthStore((s) => s.status)
  const uid = useAuthStore((s) => s.user?.uid)
  const appleToken = useAuthStore((s) => s.user?.appleToken ?? null)
  const status = useSubscriptionStore((s) => s.status)
  const hasAccess = useSubscriptionStore((s) => s.hasAccess)
  const isCanceled = useSubscriptionStore((s) => s.isCanceled)
  const verify = useSubscriptionStore((s) => s.verify)

  useEffect(() => {
    if (authStatus === "authenticated" && uid) {
      // Apple sign-in: subscriptions are keyed by the Apple token; otherwise by uid.
      verify(appleToken ?? uid)
    }
  }, [authStatus, uid, appleToken, verify])

  return {
    isVerifying: status === "idle" || status === "verifying",
    hasAccess,
    isCanceled,
  }
}
